import type { AdminStartupListItem } from '@workspace/api-backend'
import { Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useAdminStartupsQuery } from '../../admin/hooks/use-admin-api'

interface StartupSearchPickerProps {
  /** Startup IDs already in the allowlist — they're filtered out of suggestions. */
  excludeIds: string[]
  onSelect: (startup: { id: string; name: string }) => void
  placeholder?: string
  disabled?: boolean
}

/**
 * Search-and-pick startup input. Wraps the existing admin startup list query
 * (server-side search, paginated). Renders a debounced suggestions popover
 * below the input. Selecting a startup invokes `onSelect`; the parent owns
 * deduplication and persistence.
 */
export function StartupSearchPicker({
  excludeIds,
  onSelect,
  placeholder = 'Search startups by name…',
  disabled = false,
}: StartupSearchPickerProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close popover when clicking outside.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const trimmed = query.trim()
  const startupsQuery = useAdminStartupsQuery({
    page: 1,
    limit: 10,
    search: trimmed.length >= 2 ? trimmed : undefined,
  })

  const excluded = new Set(excludeIds)
  const results: AdminStartupListItem[] =
    startupsQuery.data?.data?.items?.filter((s) => !excluded.has(s.id)) ?? []

  return (
    <div ref={containerRef} className="relative">
      <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full rounded-lg border border-border bg-card py-2 pl-10 pr-4 text-sm outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-[#f97316] disabled:opacity-50"
      />

      {open && trimmed.length >= 2 && (
        <div className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
          {startupsQuery.isLoading && (
            <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
          )}
          {!startupsQuery.isLoading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No startups match.</div>
          )}
          {results.map((startup) => (
            <button
              key={startup.id}
              type="button"
              onClick={() => {
                onSelect({ id: startup.id, name: startup.name })
                setQuery('')
                setOpen(false)
              }}
              className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-elevated"
            >
              <span className="text-foreground">{startup.name}</span>
              <span className="font-mono text-xs text-muted-foreground">
                {startup.id.slice(0, 8)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
